import axios from 'axios';
import { API_ENDPOINTS, SERVICE_TYPES } from '../constants/orderFormConstants';
import { createOrderData, createFastOrderData } from '../utils/orderFormUtils';

/**
 * Service for handling order operations
 */
export class OrderService {
  /**
   * Process payment deduction
   * @param {string} userId - User ID
   * @param {number} amount - Amount to deduct
   * @returns {Promise<boolean>} - Success status
   */
  static async deductPayment(userId, amount) {
    try {
      const response = await axios.post(
        `${API_ENDPOINTS.FAST_URL}/orderpayment.php`,
        { userId, amount }
      );
      return response.data.success;
    } catch (error) {
      console.error("Error deducting payment:", error);
      return false;
    }
  }

  /**
   * Submit order to external API
   * @param {Object} orderData - Order data
   * @param {string} serviceType - Service type (instant/normal)
   * @returns {Promise<Object>} - Response data
   */
  static async submitExternalOrder(orderData, serviceType) {
    const apiUrl = serviceType === SERVICE_TYPES.INSTANT
      ? `${API_ENDPOINTS.FAST_URL}/orders.php`
      : `${API_ENDPOINTS.BACKEND_URL}/wp-json/wc/v3/orders`;

    if (serviceType === SERVICE_TYPES.INSTANT) {
      return await axios.post(apiUrl, orderData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
    } else {
      return await axios.post(apiUrl, orderData, {
        auth: {
          username: process.env.REACT_APP_CUSTOMER_KEY,
          password: process.env.REACT_APP_CUSTOMER_SECRET,
        },
      });
    }
  }

  /**
   * Submit order to WooCommerce
   * @param {Object} orderData - Order data
   * @returns {Promise<Object>} - Response data
   */
  static async submitWooCommerceOrder(orderData) {
    return await axios.post(
      `${API_ENDPOINTS.BACKEND_URL}/wp-json/wc/v3/orders`,
      orderData,
      {
        auth: {
          username: process.env.REACT_APP_CUSTOMER_KEY,
          password: process.env.REACT_APP_CUSTOMER_SECRET,
        },
      }
    );
  }

  /**
   * Process complete order submission
   * @param {Object} params - Order parameters
   * @returns {Promise<Object>} - Result object
   */
  static async processOrder({
    customer_id,
    service,
    product_id,
    quantity,
    price,
    youtube_url,
    service_type,
    serviceId,
    userId,
    walletBalance
  }) {
    // Validation
    try {
      // Step 1: Deduct payment
      const paymentDeducted = await this.deductPayment(userId, price);
      if (!paymentDeducted) {
        return { success: false, error: "Failed to deduct money from the wallet" };
      }

      // Step 2: Create order data
      const orderData = createOrderData({
        customer_id,
        service,
        product_id,
        quantity,
        price,
        youtube_url,
        service_type,
        serviceId
      });

      // Step 3: Submit to external API
      if (service_type === SERVICE_TYPES.INSTANT) {
        const fastOrderData = createFastOrderData({
          serviceId,
          youtube_url,
          quantity,
          userId,
          price
        });

        const externalResponse = await this.submitExternalOrder(fastOrderData, service_type);

        // Add order ID to WooCommerce order data
        orderData.meta_data.push({
          key: "order_id",
          value: externalResponse.data.order,
        });

        // Submit to WooCommerce
        await this.submitWooCommerceOrder(orderData);

        return { success: true, data: externalResponse.data };
      } else {
        // Manual order - only submit to WooCommerce
        orderData.meta_data.push({
          key: "order_id",
          value: 0,
        });
        orderData.status = "manual";

        await this.submitWooCommerceOrder(orderData);

        return { success: true, data: { message: "Manual order created successfully" } };
      }
    } catch (error) {
      console.error("Error processing order:", error);
      return { success: false, error: "Failed to create order" };
    }
  }
}
